import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, CircularProgress, IconButton, FormControl, Select, MenuItem, Typography, Chip, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { RootState, useAppDispatch, useAppSelector } from '../../../../../state/state';
import { closeModal, toggleLoading } from '../../../../../state/ModalSlice/ModalSlice';
import { toast } from 'react-toastify';
import { collection, addDoc, query, onSnapshot, orderBy, where, writeBatch, doc, getDocs } from 'firebase/firestore';
import { db } from '../../../../../Config/firebase';

type Board = { id: string; name: string; createdAt?: number };

type CreateFollowupModalProps = {
  orderData: {
    studentName: string;
    subscriptionName: string;
    amountDue: number;
    mobileNumber: string;
    orderId: string;
    paymentLink?: string;
  } | null;
};

export default function CreateFollowupModal({ orderData }: CreateFollowupModalProps) {
  const open = useAppSelector((state: RootState) => state.ModalSlice.CreateFollowupModalOpen);
  const isLoading = useAppSelector((state: RootState) => state.ModalSlice.isLoading);
  const user = useAppSelector((state: RootState) => state.userSliceV2.data);
  const userId = user?.uid || '';
  const dispatch = useAppDispatch();

  const [boards, setBoards] = useState<Board[]>([]);
  const [selectedBoardId, setSelectedBoardId] = useState<string>('');
  const [defaultColumnId, setDefaultColumnId] = useState<string>('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [existingBoards, setExistingBoards] = useState<string[]>([]);

  // Fetch boards
  useEffect(() => {
    if (!userId) return;
    const boardsQuery = query(
      collection(db, `ServiceProviders/${userId}/InflowBoards`),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(boardsQuery, (snapshot) => {
      const data: Board[] = [];
      snapshot.forEach((d) => data.push({ id: d.id, ...(d.data() as Omit<Board, 'id'>) }));
      setBoards(data);
      
      // If no boards exist, automatically create a "Follow-up" board
      if (data.length === 0) {
        createDefaultFollowupBoard();
      } else {
        // Prioritize "Follow-up" board if it exists, otherwise select the first board
        const followupBoard = data.find(board => board.name === 'Follow-up');
        if (followupBoard) {
          setSelectedBoardId(followupBoard.id);
        } else if (!selectedBoardId) {
          setSelectedBoardId(data[0].id);
        }
      }
    });
    return () => unsubscribe();
  }, [userId]);

  // Function to create default Follow-up board
  const createDefaultFollowupBoard = async () => {
    if (!userId) return;
    
    try {
      // Create "Follow-up" board
      const boardsRef = collection(db, `ServiceProviders/${userId}/InflowBoards`);
      const boardDoc = await addDoc(boardsRef, {
        name: 'Follow-up',
        createdAt: Date.now(),
        createdBy: user?.display_name || user?.email || 'Unknown',
      });

      // Create new default columns using batch write
      const batch = writeBatch(db);
      const defaultColumns = [
        { title: 'To Initiate', index: 0 },
        { title: 'In Process', index: 1 },
        { title: 'Done', index: 2 }
      ];

      defaultColumns.forEach((column) => {
        const columnRef = doc(collection(db, `ServiceProviders/${userId}/InflowBoards/${boardDoc.id}/Columns`));
        batch.set(columnRef, {
          ...column,
          createdAt: Date.now(),
        });
      });

      await batch.commit();
      
      // Set the new board as selected
      setSelectedBoardId(boardDoc.id);
      toast.success('Follow-up board created with default columns!');
    } catch (error) {
      console.error('Error creating default board:', error);
      toast.error('Failed to create default board');
    }
  };

  // Check for duplicate order IDs across all boards
  useEffect(() => {
    if (!userId || !orderData?.orderId) return;
    
    const checkDuplicates = async () => {
      const duplicateBoards: string[] = [];
      
      for (const board of boards) {
        const itemsQuery = query(
          collection(db, `ServiceProviders/${userId}/InflowBoards/${board.id}/Items`),
          where('orderData.orderId', '==', orderData.orderId)
        );
        
        try {
          const snapshot = await getDocs(itemsQuery);
          if (!snapshot.empty) {
            duplicateBoards.push(board.name);
          }
        } catch (error) {
          console.error('Error checking duplicates for board:', board.id, error);
        }
      }
      
      setExistingBoards(duplicateBoards);
      setIsDuplicate(duplicateBoards.length > 0);
    };
    
    checkDuplicates();
  }, [userId, orderData?.orderId, boards]);

  // Fetch default column ("To Initiate") when board is selected
  useEffect(() => {
    if (!userId || !selectedBoardId) return;
    const columnsQuery = query(
      collection(db, `ServiceProviders/${userId}/InflowBoards/${selectedBoardId}/Columns`),
      orderBy('index', 'asc')
    );
    const unsubscribe = onSnapshot(columnsQuery, (snapshot) => {
      snapshot.forEach((d) => {
        const columnData = d.data();
        if (columnData.title === 'To Initiate') {
          setDefaultColumnId(d.id);
        }
      });
    });
    return () => unsubscribe();
  }, [userId, selectedBoardId]);

  const handleClose = () => {
    dispatch(closeModal('CreateFollowupModalOpen'));
    setSelectedBoardId('');
    setDefaultColumnId('');
    setIsDuplicate(false);
    setExistingBoards([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !selectedBoardId || !defaultColumnId || !orderData) {
      toast.error('Missing required information');
      return;
    }

    // Double-check for duplicates before creating
    if (isDuplicate) {
      toast.error('This order already has a follow-up card. Please check existing boards.');
      return;
    }

    try {
      dispatch(toggleLoading(true));
      
      // Final duplicate check before creating
      const finalDuplicateCheck = query(
        collection(db, `ServiceProviders/${userId}/InflowBoards/${selectedBoardId}/Items`),
        where('orderData.orderId', '==', orderData.orderId)
      );
      
      const finalSnapshot = await getDocs(finalDuplicateCheck);
      if (!finalSnapshot.empty) {
        toast.error('A follow-up card for this order already exists in the selected board.');
        dispatch(toggleLoading(false));
        return;
      }
      
      const itemsRef = collection(db, `ServiceProviders/${userId}/InflowBoards/${selectedBoardId}/Items`);
      await addDoc(itemsRef, {
        title: `Follow-up: ${orderData.studentName}`,
        columnId: defaultColumnId,
        order: Date.now(),
        createdAt: Date.now(),
        createdBy: user?.display_name || user?.email || 'Unknown',
        orderData: {
          ...orderData,
          paymentLink: orderData.paymentLink || undefined
        }, // Store original order data for reference
      });
      
      toast.success('Follow-up card created successfully!');
      handleClose();
    } catch (e) {
      console.error(e);
      toast.error('Failed to create follow-up card');
    } finally {
      dispatch(toggleLoading(false));
    }
  };

  if (!orderData) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        p: 3, 
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
      }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
            Create Follow-up Card
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontSize: '13px' }}>
            Track customers who need manual follow-up attention
          </Typography>
        </Box>
        <IconButton 
          onClick={handleClose} 
          size="small"
          sx={{ 
            color: '#64748b',
            '&:hover': { backgroundColor: 'rgba(100, 116, 139, 0.08)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      
      <DialogContent sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {/* Duplicate Warning */}
          {isDuplicate && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: '8px' }}>
              This order already has a follow-up card in: {existingBoards.join(', ')}
            </Alert>
          )}

          {/* Order Information Display - More compact and modern */}
          <Box sx={{ 
            mb: 3, 
            p: 2, 
            bgcolor: '#f8fafc', 
            borderRadius: '12px', 
            border: '1px solid #e2e8f0'
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#334155' }}>
              Order Details
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
              <Chip 
                label={orderData.studentName} 
                size="small" 
                sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 500 }}
              />
              <Chip 
                label={orderData.subscriptionName} 
                size="small" 
                sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 500 }}
              />
              <Chip 
                label={`₹${orderData.amountDue}`} 
                size="small" 
                sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }}
              />
            </Box>
            {orderData.paymentLink && (
              <Typography variant="caption" sx={{ 
                display: 'block', 
                mt: 1,
                color: '#475569',
                '& a': { 
                  color: '#2563eb', 
                  textDecoration: 'underline',
                  fontWeight: 500,
                  '&:hover': { color: '#1d4ed8' }
                }
              }}>
                <strong>Payment:</strong> <a href={orderData.paymentLink} target="_blank" rel="noopener noreferrer">Click to pay</a>
              </Typography>
            )}
          </Box>

          {/* Board Selection */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: '#374151' }}>
              Select Board
            </Typography>
            <FormControl fullWidth>
              <Select
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(e.target.value as string)}
                disabled={isLoading || !boards.length}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '&:hover fieldset': {
                      borderColor: '#667eea',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#667eea',
                    },
                  },
                }}
              >
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>{board.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {selectedBoardId && (
            <Typography variant="body2" sx={{ 
              mb: 3, 
              fontSize: '13px', 
              color: '#6b7280',
              p: 1.5,
              bgcolor: '#f9fafb',
              borderRadius: '6px',
              border: '1px solid #f3f4f6'
            }}>
              Card will be created in the "To Initiate" column of the selected board
            </Typography>
          )}

          <Button 
            type="submit" 
            variant="contained" 
            fullWidth 
            disabled={isLoading || !selectedBoardId || !defaultColumnId} 
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 500,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af',
              },
            }}
          >
            {isLoading ? 'Creating...' : 'Create Follow-up Card'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
